import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Camera, RefreshCw, Loader2, Settings, Save, Brain, Heart } from 'lucide-react'
import { useWorldSimulatorStore } from '../stores/worldSimulatorStore'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import { DoubaoImageServiceV2 } from '../services/doubaoImageServiceV2'
import { RealAIService } from '../services/realAiService'
import worldAIService from '../services/worldAIService'
import { getWorldSimulatorSettings, outputLengthConfig } from '../components/WorldSimulatorSettings'
import { ExperienceSettingsDialog, getExperienceModelSettings } from '../components/ExperienceSettingsDialog'
import toast from 'react-hot-toast'

export const WorldExperience = () => {
  const { worldId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  // 从路由状态获取角色信息
  const initialPlayerCharacter = location.state?.playerCharacter || null
  const [playerCharacter, setPlayerCharacter] = useState(initialPlayerCharacter)
  const {
    currentWorld,
    currentSession,
    currentParagraph,
    currentBackgroundImage,
    currentOptions,
    quantitativeValues,
    startSession,
    selectWorld,
    setCurrentParagraph,
    setCurrentBackgroundImage,
    setOptions,
    updateQuantitativeValue
  } = useWorldSimulatorStore()

  const [isLoading, setIsLoading] = useState(true)
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [imageProgress, setImageProgress] = useState(0)
  const [autoGenerateImage, setAutoGenerateImage] = useState(false) // 默认不自动生成
  
  // 对话流相关状态
  const [messages, setMessages] = useState([]) // 对话历史
  const [userInput, setUserInput] = useState('') // 用户输入
  const [isTyping, setIsTyping] = useState(false) // AI正在输入
  const [streamingText, setStreamingText] = useState('') // 流式输出的文本
  const messagesEndRef = React.useRef(null) // 用于自动滚动到底部
  const messagesRef = React.useRef([]) // 用于保存最新的messages引用
  
  // 设置对话框
  const [showSettings, setShowSettings] = useState(false)
  const [memorySummary, setMemorySummary] = useState('') // 记忆摘要

  useEffect(() => {
    if (location.state?.playerCharacter) {
      setPlayerCharacter(location.state.playerCharacter)
    }
  }, [location.state])

  // 初始化：加载世界和创建会话
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true)

        // 加载世界
        const world = await worldSimulatorStorageService.getWorld(worldId)
        if (!world) {
          toast.error('世界不存在')
          navigate('/world-simulator')
          return
        }

        // 选择世界
        selectWorld(world)

        // 获取用户ID（默认使用'guest'）
        const userId = 'guest' // 后续可以从AuthService获取

        // 检查是否有已存在的会话
        let session = currentSession
        
        // 如果路由state中有sessionId，优先使用
        const sessionIdFromState = location.state?.sessionId
        
        if (sessionIdFromState) {
          session = await worldSimulatorStorageService.getSession(sessionIdFromState)
          if (session) {
            console.log('✅ 从路由state恢复会话:', session.sessionId)
            // 更新store中的会话
            useWorldSimulatorStore.setState({ currentSession: session })
            
            if (session.playerCharacter) {
              setPlayerCharacter(session.playerCharacter)
            }
            
            // 恢复会话状态
            if (session.currentParagraph) {
              setCurrentParagraph(session.currentParagraph)
              setMessages([{
                role: 'assistant',
                content: session.currentParagraph,
                timestamp: session.lastActiveTime
              }])
            } else {
              await loadInitialContent(world, session)
              setIsLoading(false)
              return
            }
            
            if (session.backgroundImage) {
              setCurrentBackgroundImage(session.backgroundImage)
            }
            
            if (session.quantitativeValues) {
              Object.entries(session.quantitativeValues).forEach(([name, value]) => {
                updateQuantitativeValue(name, value)
              })
            }
            
            if (session.conversationHistory && session.conversationHistory.length > 0) {
              setMessages(session.conversationHistory.map(msg => ({
                role: msg.role || (msg.isUser ? 'user' : 'assistant'),
                content: msg.content,
                timestamp: msg.timestamp || Date.now()
              })))
            }
            
            // 生成选项
            const options = await generateOptions(session.currentParagraph || '', world)
            setOptions(options)
            
            setIsLoading(false)
            return
          }
        }
        
        if (!session) {
          // 尝试获取用户在这个世界的最新会话
          const userSessions = await worldSimulatorStorageService.getUserSessions(userId, worldId)
          if (userSessions && userSessions.length > 0) {
            // 找到最新的活跃会话
            const activeSession = userSessions.find(s => s.status === 'active')
            if (activeSession) {
              session = activeSession
              console.log('✅ 恢复已存在的会话:', session.sessionId)
              
              if (session.playerCharacter) {
                setPlayerCharacter(session.playerCharacter)
              }
              
              // 恢复会话状态
              if (session.currentParagraph) {
                setCurrentParagraph(session.currentParagraph)
                setMessages([{
                  role: 'assistant',
                  content: session.currentParagraph,
                  timestamp: session.lastActiveTime
                }])
              } else {
                await loadInitialContent(world, session)
                setIsLoading(false)
                return
              }
              
              if (session.backgroundImage) {
                setCurrentBackgroundImage(session.backgroundImage)
              }
              
              if (session.quantitativeValues) {
                Object.entries(session.quantitativeValues).forEach(([name, value]) => {
                  updateQuantitativeValue(name, value)
                })
              }
              
              if (session.conversationHistory && session.conversationHistory.length > 0) {
                setMessages(session.conversationHistory.map(msg => ({
                  role: msg.role || (msg.isUser ? 'user' : 'assistant'),
                  content: msg.content,
                  timestamp: msg.timestamp || Date.now()
                })))
              }
              
              // 恢复记忆摘要
              if (session.memorySummary) {
                setMemorySummary(session.memorySummary)
                console.log('✅ 已恢复记忆摘要')
              }
              
              // 恢复选项（如果有）
              if (session.choiceHistory && session.choiceHistory.length > 0) {
                // 可以基于最后的选择生成新选项
                const lastChoice = session.choiceHistory[session.choiceHistory.length - 1]
                const options = await generateOptions(session.currentParagraph || '', world)
                setOptions(options)
              } else {
                // 生成初始选项
                const options = await generateOptions(session.currentParagraph || '', world)
                setOptions(options)
              }
              
              // 更新store中的会话
              useWorldSimulatorStore.setState({ currentSession: session })
              
              setIsLoading(false)
              return
            }
          }
          
          // 如果没有已存在的会话，创建新会话
          session = await startSession(worldId, userId)
        }

        // 初始化段落和选项（仅当是新会话时）
        if (!session.currentParagraph) {
          await loadInitialContent(world, session)
        }

      } catch (error) {
        console.error('初始化失败:', error)
        toast.error('初始化失败')
      } finally {
        setIsLoading(false)
      }
    }

    if (worldId) {
      init()
    }
  }, [worldId, startSession, selectWorld])

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, streamingText])

  // 生成起点选择选项（基于世界设定）
  const generateStartOptions = async (world, character = null) => {
    try {
      // 如果世界有地区设定，基于地区生成起点
      if (world.regions && world.regions.length > 0) {
        const options = world.regions.map((region, index) => ({
          id: `start-${index}`,
          region: region.name,
          description: region.description || `从${region.name}开始你的冒险`,
          character: null,
          situation: `你出现在${region.name}`
        }))
        
        // 如果有角色设定，可以组合生成更丰富的起点
        if (world.characters && world.characters.length > 0) {
          // 为每个地区添加一些角色相关的起点选项
          const characterOptions = world.characters.slice(0, 3).map((char, index) => ({
            id: `start-char-${index}`,
            region: world.regions[0]?.name || '未知地区',
            character: char.name,
            description: `作为${char.name}的${char.role || '伙伴'}开始`,
            situation: `你与${char.name}相遇`
          }))
          
          return [...options, ...characterOptions].slice(0, 6) // 最多6个选项
        }
        
        let enrichedOptions = options.slice(0, 4)
        
        if (character?.name) {
          enrichedOptions = enrichedOptions.map(opt => ({
            ...opt,
            description: `${opt.description}。${character.name}踏入此地时，命运开始流动。`
          }))
        }
        
        return enrichedOptions
      }
      
      // 如果没有地区设定，生成通用起点
      return [
        {
          id: 'start-default',
          region: '起始地点',
          description: '开始你的冒险',
          character: null,
          situation: '你开始了在这个世界的旅程'
        }
      ]
    } catch (error) {
      console.error('生成起点选项失败:', error)
      return []
    }
  }

  const selectAutoStartOption = (options, character = null) => {
    if (!options || options.length === 0) return null
    
    if (!character) return options[0]
    
    const backgroundText = character.background?.toLowerCase() || ''
    const roleText = character.role?.toLowerCase() || ''
    
    const backgroundMatch = options.find(
      (opt) =>
        (opt.region && backgroundText.includes(opt.region.toLowerCase())) ||
        (opt.character && roleText.includes(opt.character.toLowerCase()))
    )
    
    return backgroundMatch || options[0]
  }

  // 加载初始内容
  const loadInitialContent = async (world, session) => {
    try {
      // 先生成起点选择选项
      const startOpts = await generateStartOptions(world, playerCharacter)
      const selected = selectAutoStartOption(startOpts, playerCharacter)
      await startAdventure(world, selected)
    } catch (error) {
      console.error('加载初始内容失败:', error)
    }
  }

  // 开始冒险（基于选定的起点）
  const startAdventure = async (world, start = null) => {
    try {
      // 生成初始段落（基于选定的起点和角色信息）
      const initialParagraph = await generateInitialParagraph(world, start, playerCharacter)
      
      // 添加到消息列表
      setMessages([{
        role: 'assistant',
        content: initialParagraph,
        timestamp: Date.now()
      }])
      
      setCurrentParagraph(initialParagraph)

      // 如果启用自动生成，生成背景图片
      if (autoGenerateImage) {
        await generateBackgroundImage(initialParagraph, world)
      }

      // 生成初始选项（基于世界设定）
      const options = await generateOptions(initialParagraph, world)
      setOptions(options)

      // 初始化量化数值
      if (world.quantitativeSystems && world.quantitativeSystems.length > 0) {
        const initialValues = {}
        world.quantitativeSystems.forEach(system => {
          initialValues[system.name] = system.initial || 100
        })
        Object.entries(initialValues).forEach(([name, value]) => {
          updateQuantitativeValue(name, value)
        })
      }
    } catch (error) {
      console.error('开始冒险失败:', error)
    }
  }

  // 生成初始段落（基于世界设定）
  const generateInitialParagraph = async (world, selectedStart = null, character = null) => {
    try {
      // 获取用户设置
      const settings = getWorldSimulatorSettings()
      
      // 使用worldAIService构建完整的系统提示词（传入设置）
      const systemPrompt = worldAIService.buildSystemPrompt(world, null, settings)
      
      // 构建基于世界设定的用户消息
      let userMessage = `请为玩家生成这个世界的开场段落。

世界名称：${world.name || '未知世界'}
世界描述：${world.description || world.summary || ''}

`
      
      // 如果有选定的起点，加入起点信息
      if (selectedStart) {
        userMessage += `玩家选择的起点：
- 地区：${selectedStart.region || '未指定'}
- 角色身份：${selectedStart.character || '未指定'}
- 起始情境：${selectedStart.situation || '未指定'}

`
      }
      
      // 添加世界架构摘要
      if (world.preText) {
        userMessage += `世界架构摘要（节选）：
${world.preText.slice(0, 800)}

`
      }
      
      // 根据设置决定是否添加玩家角色信息（起始段落默认包含关键设定）
      if (character) {
        userMessage += `玩家角色信息：
- 姓名：${character.name}
- 身份/职业：${character.role || '未指定'}
- 背景：${character.background || '未指定'}
- 性格：${character.personality || '未指定'}
- 目标：${character.goals || '未指定'}

`
        userMessage += `重要规则：
1. 该玩家角色姓名固定为「${character.name}」，不可替换、不可创造新外号。
2. 叙事时可以使用第二人称“你”，若使用第三人称，必须使用「${character.name}」。
3. 严禁出现任何其他玩家姓名或别名。

`
        
        if (!settings.showCharacterInfo) {
          userMessage += '（即使角色信息展示被关闭，以上信息仅用于AI构思开场，不会直接呈现给玩家）\n\n'
        }
      }
      
      // 根据设置决定是否添加世界设定信息
      if (settings.showWorldInfo) {
        if (world.regions && world.regions.length > 0) {
          userMessage += `主要地区：\n${world.regions.map(r => `- ${r.name}: ${r.description || ''}`).join('\n')}\n\n`
        }
        
        if (world.characters && world.characters.length > 0) {
          userMessage += `重要角色：\n${world.characters.map(c => `- ${c.name} (${c.role || ''}): ${c.description || ''}`).join('\n')}\n\n`
        }
      }
      
      // 根据设置决定是否添加状态信息
      if (settings.showStatusInfo && world.quantitativeSystems && world.quantitativeSystems.length > 0) {
        userMessage += `数值系统：${world.quantitativeSystems.map(s => s.name).join('、')}\n\n`
      }
      
      // 获取输出长度配置
      const lengthConfig = outputLengthConfig[settings.outputLength] || outputLengthConfig.medium
      
      userMessage += `要求：
1. 严格遵循世界的世界观设定（${world.preText ? '已提供' : '参考世界描述'}）
2. 符合世界的规则和系统
3. 如果指定了起点，必须从该起点开始
4. 营造沉浸感，让玩家感受到这个世界的独特氛围
5. 为玩家提供清晰的初始情境和可行动的方向

请生成一个引人入胜的开场段落（${lengthConfig.min}-${lengthConfig.max}字）。`
      
      // 构建消息数组
      const messagesArray = []
      if (systemPrompt) {
        messagesArray.push({
          role: 'system',
          content: systemPrompt
        })
      }
      messagesArray.push({
        role: 'user',
        content: userMessage
      })
      
      // 调用AI生成（传递messages数组）
      const response = await RealAIService.callLanguageModel(
        messagesArray.length > 1 ? messagesArray : userMessage,
        { 
          temperature: 0.8,
          maxTokens: settings.outputLength === 'very-long' ? 2048 : 
                     settings.outputLength === 'long' ? 1536 :
                     settings.outputLength === 'short' ? 512 : 1024
        }
      )
      
      // 解析AI响应
      const parsedResponse = worldAIService.parseAIResponse(response, world)
      
      return parsedResponse.paragraph || response.trim()
    } catch (error) {
      console.error('生成段落失败:', error)
      return world.description || world.summary || '欢迎来到这个世界...'
    }
  }

  // 生成背景图片
  const generateBackgroundImage = async (paragraph, world, onProgress) => {
    try {
      setIsGeneratingImage(true)
      setImageProgress(10)

      // 构建图片生成提示词
      const imagePrompt = await buildImagePrompt(paragraph, world)
      setImageProgress(30)

      // 调用豆包图生图API
      const imageUrl = await DoubaoImageServiceV2.generateImage(imagePrompt, {
        aspectRatio: '16:9',
        quality: 'high'
      })

      setImageProgress(100)
      setCurrentBackgroundImage(imageUrl)
      toast.success('背景图片生成完成')

    } catch (error) {
      console.error('生成背景图片失败:', error)
      toast.error('图片生成失败')
    } finally {
      setIsGeneratingImage(false)
      setImageProgress(0)
    }
  }

  // 构建图片提示词
  const buildImagePrompt = async (paragraph, world) => {
    const prompt = `请为以下故事段落生成一张视觉化画面的英文描述：

世界类型：${world.type}
世界风格：${world.style?.join(', ') || 'fantasy'}
当前段落：${paragraph}

要求：
1. 描述画面的主体内容和构图
2. 包含环境氛围和光线效果
3. 符合${world.type}的世界观设定
4. 16:9横向构图，适合作为全屏背景
5. 高质量、电影感、细节丰富

请直接输出英文图片描述（用于AI绘画，不要包含其他内容）。`

    try {
      const imagePrompt = await RealAIService.callLanguageModel(prompt)
      return imagePrompt.trim()
    } catch (error) {
      console.error('构建图片提示词失败:', error)
      // 降级到简单提示词
      return `A cinematic scene from ${world.name}, ${paragraph}, 16:9 aspect ratio, high quality, detailed`
    }
  }

  // 生成选项（基于世界设定）
  const generateOptions = async (paragraph, world) => {
    try {
      // 获取用户设置
      const settings = getWorldSimulatorSettings()
      
      // 使用worldAIService构建系统提示词（传入设置）
      const systemPrompt = worldAIService.buildSystemPromptWithMemory(world, null, settings, [], '')
      
      // 构建基于世界设定的选项生成提示
      let prompt = `基于以下段落和世界设定，生成3-4个玩家可以选择的行动选项：

当前段落：${paragraph.substring(0, 500)}

世界设定：
- 世界名称：${world.name}
- 世界观：${world.preText ? '已提供详细世界观设定' : world.description || world.summary || ''}

`
      
      // 添加地区信息
      if (world.regions && world.regions.length > 0) {
        prompt += `可探索的地区：${world.regions.map(r => r.name).join('、')}\n`
      }
      
      // 添加角色信息
      if (world.characters && world.characters.length > 0) {
        prompt += `重要角色：${world.characters.map(c => c.name).join('、')}\n`
      }
      
      // 添加系统规则
      if (world.quantitativeSystems && world.quantitativeSystems.length > 0) {
        prompt += `数值系统：${world.quantitativeSystems.map(s => s.name).join('、')}\n`
      }
      
      prompt += `
要求：
1. 每个选项10-20字，清晰明确
2. 选项必须符合世界的世界观和规则
3. 选项要有不同的方向性（探索、互动、行动等）
4. 选项应该与当前段落的情境相关
5. 如果世界有特定系统（如声望、资源等），选项可以涉及这些系统
6. 严格遵循世界的设定，不要出现不符合世界观的内容

返回JSON数组格式：["选项1", "选项2", "选项3", "选项4"]`

      const response = await RealAIService.callLanguageModel(prompt, systemPrompt)
      
      // 尝试解析JSON
      let options = []
      try {
        const jsonMatch = response.match(/\[[\s\S]*?\]/)
        if (jsonMatch) {
          options = JSON.parse(jsonMatch[0])
        } else {
          // 如果没有JSON，尝试提取选项
          const lines = response.split('\n').filter(line => line.trim())
          options = lines
            .filter(line => /^[A-D]\.|^[0-9]\.|^[-•]/.test(line.trim()))
            .map(line => line.replace(/^[A-D0-9]\.\s*|^[-•]\s*/, '').trim())
            .filter(opt => opt.length > 0)
            .slice(0, 4)
        }
      } catch (e) {
        console.warn('解析选项失败，使用降级方案', e)
      }
      
      // 如果解析失败，使用AI重新生成
      if (options.length === 0) {
        const fallbackPrompt = `请基于以下内容生成3个行动选项，每个选项10-15字，返回JSON数组：\n\n${paragraph.substring(0, 300)}`
        const fallbackResponse = await RealAIService.callLanguageModel(fallbackPrompt, systemPrompt)
        const jsonMatch = fallbackResponse.match(/\[[\s\S]*?\]/)
        if (jsonMatch) {
          options = JSON.parse(jsonMatch[0])
        }
      }
      
      // 确保至少有2个选项
      if (options.length === 0) {
        options = ['继续探索', '仔细观察周围']
      }
      
      return options.map((text, index) => ({
        id: `option-${index}`,
        text: text.trim(),
        index
      }))
    } catch (error) {
      console.error('生成选项失败:', error)
      // 降级到默认选项
      return [
        { id: 'option-1', text: '继续探索', index: 0 },
        { id: 'option-2', text: '仔细观察', index: 1 }
      ]
    }
  }

  // 更新messages引用
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // 保存会话状态
  const saveSessionState = async () => {
    if (!currentSession) return
    
    try {
      // 使用ref获取最新的messages
      const currentMessages = messagesRef.current
      await worldSimulatorStorageService.updateSession(currentSession.sessionId, {
        currentParagraph,
        backgroundImage: currentBackgroundImage,
        quantitativeValues,
        conversationHistory: currentMessages,
        choiceHistory: currentMessages.filter(m => m.role === 'user').map(m => m.content),
        memorySummary, // 保存记忆摘要
        playerCharacter: playerCharacter || currentSession.playerCharacter || null
      })
    } catch (error) {
      console.error('保存会话状态失败:', error)
    }
  }

  // 处理选项选择
  const handleOptionClick = async (option) => {
    try {
      // 添加用户选择到消息列表
      setMessages(prev => [...prev, {
        role: 'user',
        content: option.text,
        timestamp: Date.now()
      }])

      // 清空选项
      setOptions([])
      
      // 显示AI正在输入
      setIsTyping(true)

      // 生成下一段落
      const nextParagraph = await generateNextParagraph(option, currentWorld)
      
      // 流式显示效果
      await simulateStreaming(nextParagraph)
      
      // 添加AI回复到消息列表
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: nextParagraph,
        timestamp: Date.now()
      }])
      setCurrentParagraph(nextParagraph)
      setIsTyping(false)
      setStreamingText('')

      // 自动生成背景图片（如果启用）
      if (autoGenerateImage) {
        await generateBackgroundImage(nextParagraph, currentWorld)
      }

      // 生成新的选项
      const newOptions = await generateOptions(nextParagraph, currentWorld)
      setOptions(newOptions)

      // 保存会话状态（包含记忆摘要）
      await saveSessionState()

    } catch (error) {
      console.error('处理选项失败:', error)
      toast.error('处理选项失败')
      setIsTyping(false)
      setStreamingText('')
    }
  }

  // 处理用户自定义输入
  const handleUserInput = async () => {
    if (!userInput.trim()) return

    const input = userInput.trim()
    setUserInput('')
    
    try {
      // 添加用户消息
      setMessages(prev => [...prev, {
        role: 'user',
        content: input,
        timestamp: Date.now()
      }])

      // 清空选项
      setOptions([])
      
      // 显示AI正在输入
      setIsTyping(true)

      // 生成AI回复
      const option = { text: input }
      const nextParagraph = await generateNextParagraph(option, currentWorld)
      
      // 流式显示效果
      await simulateStreaming(nextParagraph)
      
      // 添加AI回复
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: nextParagraph,
        timestamp: Date.now()
      }])
      setCurrentParagraph(nextParagraph)
      setIsTyping(false)
      setStreamingText('')

      // 自动生成背景图片（如果启用）
      if (autoGenerateImage) {
        await generateBackgroundImage(nextParagraph, currentWorld)
      }

      // 生成新的选项
      const newOptions = await generateOptions(nextParagraph, currentWorld)
      setOptions(newOptions)

      // 保存会话状态（包含记忆摘要）
      await saveSessionState()

    } catch (error) {
      console.error('处理用户输入失败:', error)
      toast.error('处理失败')
      setIsTyping(false)
      setStreamingText('')
    }
  }

  // 模拟流式输出效果
  const simulateStreaming = async (text) => {
    const chars = text.split('')
    for (let i = 0; i < chars.length; i++) {
      setStreamingText(prev => prev + chars[i])
      await new Promise(resolve => setTimeout(resolve, 20)) // 20ms per character
    }
  }

  // 生成下一段落（基于世界设定）
  const generateNextParagraph = async (option, world) => {
    try {
      // 获取用户设置
      const settings = getWorldSimulatorSettings()
      
      // 获取当前对话历史
      const currentMessages = messagesRef.current || messages
      const conversationHistory = worldAIService.buildConversationHistory(currentMessages, 20)
      
      // 生成记忆摘要（每10条消息生成一次）
      let newMemorySummary = ''
      if (currentMessages.length >= 10 && currentMessages.length % 10 === 0) {
        try {
          newMemorySummary = await worldAIService.generateMemorySummary(currentMessages, world, 10)
          console.log('📝 已生成记忆摘要:', newMemorySummary.substring(0, 100))
          // 更新记忆摘要状态
          setMemorySummary(newMemorySummary)
        } catch (error) {
          console.warn('生成记忆摘要失败:', error)
        }
      }
      
      // 如果有会话中保存的记忆摘要，也使用它
      let combinedMemorySummary = memorySummary
      if (newMemorySummary) {
        combinedMemorySummary = memorySummary ? `${memorySummary}\n\n${newMemorySummary}` : newMemorySummary
      }
      
      // 构建系统提示词（包含记忆和设置）
      const systemPrompt = worldAIService.buildSystemPromptWithMemory(
        world, 
        currentSession, 
        settings, 
        conversationHistory,
        combinedMemorySummary
      )
      
      // 构建用户消息，包含上下文和世界设定
      let userMessage = `玩家选择了：${option.text}\n\n`
      
      // 添加上下文信息
      if (currentParagraph) {
        userMessage += `当前情境：${currentParagraph.substring(0, 300)}\n\n`
      }
      
      // 根据设置决定是否添加世界设定信息
      if (settings.showWorldInfo) {
        userMessage += `世界设定：
- 世界名称：${world.name}
- 世界观：${world.preText ? '已提供详细设定' : world.description || world.summary || ''}\n\n`
      }
      
      // 根据设置决定是否添加角色信息
      if (playerCharacter) {
        userMessage += `玩家角色：${playerCharacter.name}（${playerCharacter.role || '未指定'}）\n`
        userMessage += `重要规则：
1. 玩家姓名固定为「${playerCharacter.name}」，严禁更改或创造新名字。
2. 如果需要第三人称描述，必须使用「${playerCharacter.name}」；否则请直接使用第二人称“你”。
3. 若AI自动生成了其他姓名，请立即改回玩家提供的名字。

`
      }
      
      // 根据设置决定是否添加状态信息
      if (settings.showStatusInfo && Object.keys(quantitativeValues).length > 0) {
        const statusText = Object.entries(quantitativeValues)
          .map(([name, value]) => `${name}: ${value}`)
          .join('、')
        userMessage += `当前状态：${statusText}\n\n`
      }
      
      // 获取输出长度配置
      const lengthConfig = outputLengthConfig[settings.outputLength] || outputLengthConfig.medium
      
      // 添加世界设定提醒
      userMessage += `请确保：
1. 严格遵循世界的世界观设定（${world.preText ? '已提供详细设定' : '参考世界描述'}）
2. 符合世界的规则和系统
3. 如果世界有地区设定，确保地点描述符合设定
4. 如果世界有角色设定，确保角色行为符合设定
5. 保持沉浸感和连贯性

请生成下一段内容（${lengthConfig.min}-${lengthConfig.max}字）。`
      
      // 构建完整的消息数组（包含对话历史）
      const messagesArray = []
      
      // 添加系统提示词（作为第一条消息）
      if (systemPrompt) {
        messagesArray.push({
          role: 'system',
          content: systemPrompt
        })
      }
      
      // 添加对话历史（最近20条，排除最后一条用户消息）
      const historyToInclude = conversationHistory.slice(0, -1) // 排除当前用户输入
      messagesArray.push(...historyToInclude)
      
      // 添加当前用户消息
      messagesArray.push({
        role: 'user',
        content: userMessage
      })
      
      // 调用AI生成（传递messages数组）
      const response = await RealAIService.callLanguageModel(
        messagesArray.length > 1 ? messagesArray : userMessage, // 如果有多条消息，传递数组；否则传递字符串
        { 
          temperature: 0.8,
          maxTokens: settings.outputLength === 'very-long' ? 2048 : 
                     settings.outputLength === 'long' ? 1536 :
                     settings.outputLength === 'short' ? 512 : 1024
        }
      )
      
      // 解析AI响应
      const parsedResponse = worldAIService.parseAIResponse(response, world)
      
      return parsedResponse.paragraph || response.trim()
    } catch (error) {
      console.error('生成下一段落失败:', error)
      
      // 降级方案：使用简单提示词
      try {
        const settings = getWorldSimulatorSettings()
        const lengthConfig = outputLengthConfig[settings.outputLength] || outputLengthConfig.medium
        const prompt = `基于以下信息，生成下一段故事内容：

当前段落：${currentParagraph}
玩家选择：${option.text}
世界设定：${world.name}

要求：
1. ${lengthConfig.min}-${lengthConfig.max}字
2. 承接上一段，体现玩家的选择
3. 为下一步互动做铺垫
4. 直接输出段落内容`

        const response = await RealAIService.callLanguageModel(prompt)
        return response.trim()
      } catch (fallbackError) {
        console.error('降级生成也失败:', fallbackError)
        return '故事继续...'
      }
    }
  }

  // 手动生成图片
  const handleGenerateImage = async () => {
    if (currentParagraph) {
      await generateBackgroundImage(currentParagraph, currentWorld)
    }
  }

  // 设置对话框回调函数
  const handleClearHistory = () => {
    setMessages([])
    setMemorySummary('')
    setCurrentParagraph('')
    setOptions([])
    toast.success('聊天记录已清空')
  }

  const handleExportHistory = () => {
    console.log('导出历史记录')
  }

  const handleRefreshMemory = async () => {
    if (messages.length < 5) {
      toast.error('对话内容太少，无法生成记忆摘要')
      return
    }
    
    try {
      const newMemorySummary = await worldAIService.generateMemorySummary(messages, currentWorld, messages.length)
      setMemorySummary(newMemorySummary)
      toast.success('记忆摘要已更新')
    } catch (error) {
      console.error('生成记忆摘要失败:', error)
      toast.error('生成记忆摘要失败')
    }
  }

  if (isLoading) {
    return (
      <div className="fixed top-16 inset-x-0 bottom-0 bg-dark-900 flex items-center justify-center z-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-dark-300">加载世界中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed top-16 inset-x-0 bottom-0 bg-dark-900 flex flex-col z-50">
      {/* 背景图片 */}
      {currentBackgroundImage && (
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${currentBackgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(8px)'
          }}
        />
      )}

      {/* 主容器 */}
      <div className="relative z-10 flex flex-col h-full">
        {/* 顶部栏 */}
        <div className="flex-shrink-0 bg-dark-900/80 backdrop-blur-sm border-b border-dark-700 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            {/* 左侧：返回按钮和世界名称 */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/world-simulator')}
                className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-white">{currentWorld?.name || '世界体验'}</h1>
              </div>
            </div>

            {/* 中间：数值显示 */}
            <div className="flex-1 max-w-3xl">
              {currentWorld?.quantitativeSystems && currentWorld.quantitativeSystems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentWorld.quantitativeSystems.slice(0, 4).map((system) => {
                    const value = quantitativeValues[system.name] ?? system.initial ?? 0
                    const min = system.min ?? 0
                    const max = system.max ?? (system.name?.toLowerCase().includes('san') ? 100 : 100)
                    const percentage = max > min ? ((value - min) / (max - min)) * 100 : 0
                    const description = system.description || system.purpose || '用于追踪该世界的重要状态或资源。'
                    const changeHint = system.changeHint || system.changeDescription || '根据剧情选择和系统规则会动态增减。'

                    return (
                      <div
                        key={system.name}
                        className="bg-dark-800/70 rounded-lg border border-dark-700 px-3 py-2 flex flex-col gap-1"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-sm text-white font-medium">
                            {system.icon === 'brain' && <Brain className="w-4 h-4 text-cyan-400" />}
                            {system.icon === 'heart' && <Heart className="w-4 h-4 text-pink-400" />}
                            <span>{system.name}</span>
                          </div>
                          <span className="text-xs text-dark-300">
                            {value}/{max}{system.unit ? ` ${system.unit}` : ''}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-dark-700 rounded-full overflow-hidden">
                          <div
                            className="h-full transition-all duration-500"
                            style={{
                              width: `${Math.min(100, Math.max(0, percentage))}%`,
                              background: system.color || (system.icon === 'brain'
                                ? 'linear-gradient(90deg, #06b6d4, #22d3ee)'
                                : 'linear-gradient(90deg, #ec4899, #f472b6)')
                            }}
                          />
                        </div>
                        <p className="text-xs text-dark-300 leading-snug">
                          {description}
                        </p>
                        <p className="text-xs text-dark-400">
                          {changeHint}
                        </p>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-xs text-dark-400">
                  当前世界没有定义量化系统。
                </div>
              )}
            </div>

            {/* 右侧：工具按钮 */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleGenerateImage}
                disabled={isGeneratingImage || !currentParagraph}
                className="px-3 py-1.5 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm flex items-center gap-1.5"
              >
                <Camera className="w-4 h-4" />
                生成图片
              </button>
              <button
                onClick={() => {
                  setAutoGenerateImage(!autoGenerateImage)
                  toast.success(autoGenerateImage ? '自动生成已关闭' : '自动生成已开启')
                }}
                className={`px-3 py-1.5 rounded-lg transition-colors text-sm ${
                  autoGenerateImage 
                    ? 'bg-primary text-white' 
                    : 'bg-dark-800 hover:bg-dark-700 text-white'
                }`}
              >
                {autoGenerateImage ? '自动' : '手动'}
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all text-sm flex items-center gap-1.5 shadow-lg shadow-purple-500/20"
                title="打开设置 - 管理聊天记录、记忆设定、模型参数"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">设置</span>
              </button>
            </div>
          </div>
        </div>

        {/* 对话区域 */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-primary/20 text-white border border-primary/30'
                      : 'bg-dark-800/90 text-white border border-dark-700'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* 流式输出中的消息 */}
            {isTyping && streamingText && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg px-4 py-3 bg-dark-800/90 text-white border border-dark-700">
                  <p className="whitespace-pre-wrap leading-relaxed">{streamingText}<span className="animate-pulse">▊</span></p>
                </div>
              </div>
            )}

            {/* AI正在输入指示器 */}
            {isTyping && !streamingText && (
              <div className="flex justify-start">
                <div className="rounded-lg px-4 py-3 bg-dark-800/90 border border-dark-700">
                  <div className="flex items-center gap-2 text-dark-300">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">AI正在思考...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* 底部：选项和输入区 */}
        <div className="flex-shrink-0 bg-dark-900/95 backdrop-blur-sm border-t border-dark-700 px-4 py-4">
          <div className="max-w-4xl mx-auto space-y-3">
            {/* 分支选项 */}
            {currentOptions.length > 0 && !isTyping && (
              <div className="grid grid-cols-2 gap-2">
                {currentOptions.map((option) => (
                  <button
                    key={option.id}
                    className="px-4 py-2.5 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors text-left border border-dark-600 hover:border-primary/50"
                    onClick={() => handleOptionClick(option)}
                  >
                    <span className="text-sm">{option.text}</span>
                  </button>
                ))}
              </div>
            )}

            {/* 用户输入框 */}
            <div className="flex gap-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUserInput()}
                placeholder="输入你的选择或行动..."
                disabled={isTyping}
                className="flex-1 px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary disabled:opacity-50"
              />
              <button
                onClick={handleUserInput}
                disabled={!userInput.trim() || isTyping}
                className="px-6 py-2.5 bg-primary hover:bg-primary-dark rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                发送
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 图片生成加载遮罩 */}
      {isGeneratingImage && (
        <div className="fixed top-16 inset-x-0 bottom-0 bg-dark-900/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <div className="w-64 h-2 bg-dark-700 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
                style={{ width: `${imageProgress}%` }}
              />
            </div>
            <p className="text-dark-300 text-sm">
              {imageProgress < 30 ? '正在分析场景...' : imageProgress < 80 ? '正在生成图片...' : '图片生成完成'}
            </p>
          </div>
        </div>
      )}

      {/* 设置对话框 */}
      <ExperienceSettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        messages={messages}
        memorySummary={memorySummary}
        worldData={currentWorld}
        sessionId={currentSession?.sessionId || ''}
        onClearHistory={handleClearHistory}
        onExportHistory={handleExportHistory}
        onRefreshMemory={handleRefreshMemory}
      />
    </div>
  )
}

